<script lang="ts">
    import { _ } from "svelte-i18n"
    import ChevronDownIcon from "svelte-material-icons/ChevronDown.svelte"
    import CheckoutFooter from "./CheckoutFooter.svelte"
    import FormInputText from "../../widgets/FormInputText.svelte"
    import FormInputRadio from "../../widgets/FormInputRadio.svelte"
    import { cart, checkoutData, getAsyncCartSum, login } from "../../../store"
    import { onMount } from "svelte"
    import { navigate } from "../../../../../vendor/svelte-routing"
    import { getCountries, getCountry } from "../../../api"
    import MiniLoader from "../../widgets/MiniLoader.svelte"
    import { debounce, equal } from "../../../utils"
    import {
        defaultVAT,
        maxInputLength,
        paymentMethods,
        sameBillingAndDeliveryAddress,
    } from "../../../../../api/hooks/config-client"
    import { initialLocale, langField } from "../../../i18n"
    import { max } from "lodash"

    let oldCheckSame = $checkoutData.sameAddress

    let paymentForm

    const checkSame = () => {
        $checkoutData.sameAddress = equal($checkoutData.deliveryAddress, $checkoutData.billingAddress)
    }

    $: {
        if (!$cart?.length || $login) {
            // empty cart goto start
            navigate("/", { replace: true })
        } else if ($checkoutData.stepsValid < 1) {
            // incomplete data on step 1 so redirect
            navigate("/checkout", { replace: true })
        }
    }

    onMount(() => {
        if ($checkoutData.billingAddressInit) {
            // first visit of this site
            // let sameAddress true for copiing address
            delete $checkoutData.billingAddressInit
            oldCheckSame = false
        } else {
            checkSame()
        }
    })

    const invalidate = () => {
        $checkoutData.stepsValid = 1
        checkSame()
    }

    $: {
        if (!oldCheckSame && $checkoutData.sameAddress) {
            Object.keys($checkoutData.deliveryAddress).forEach((k) => {
                $checkoutData.billingAddress[k] = $checkoutData.deliveryAddress[k]
            })
        }
        oldCheckSame = $checkoutData.sameAddress
    }

    let oldCode: any
    $: {
        let code = $checkoutData.billingAddress.countryCode
        if (oldCode != code) {
            getCountry($checkoutData.billingAddress.countryCode).then((c) => {
                $checkoutData.vat = c?.vat ?? defaultVAT
            })
        }
        oldCode = code
    }

    let cartSum: CartSum

    $: (async () => {
        cartSum = await getAsyncCartSum($cart)
    })()

    // keine getrennte Rechnungsanschrift, Daten werden kopiert
    $: if (sameBillingAndDeliveryAddress && !$checkoutData.sameAddress) {
        $checkoutData.sameAddress = true
        Object.keys($checkoutData.deliveryAddress).forEach((k) => {
            $checkoutData.billingAddress[k] = $checkoutData.deliveryAddress[k]
        })
    }
</script>

<div class="subline">{$_("checkout.order")}</div>
<h2 class="h2_nooffset">{$_("checkout.payment")}</h2>

<form
    bind:this="{paymentForm}"
    on:submit|preventDefault="{() => {
        checkSame()
        $checkoutData.stepsValid = 2
        navigate('/checkout/overview')
    }}"
>
    <section>
        <div class="section_headline">{$_("checkout.payment-type")}</div>
        {#each paymentMethods as pMethod}
            <FormInputRadio
                label="{$_('checkout.' + pMethod)}"
                bind:group="{$checkoutData.payment.type}"
                value="{pMethod}"
                required="{true}"
            />
        {/each}
    </section>

    {#if !sameBillingAndDeliveryAddress}
        <section>
            <div class="section_headline">{$_("checkout.billing-address")}</div>
            <FormInputRadio
                label="{$_('checkout.same-as-shipping-address')}"
                bind:group="{$checkoutData.sameAddress}"
                value="{true}"
                checked="{true}"
            />
            <FormInputRadio
                label="{$_('checkout.different-billing-address')}"
                bind:group="{$checkoutData.sameAddress}"
                value="{false}"
            />
            <div class="row">
                <div class="col-md-6">
                    <FormInputText
                        name="billing_firstname"
                        placeholder="{$_('checkout.firstname')} / {$_('checkout.address-supplement')}"
                        bind:value="{$checkoutData.billingAddress.firstname}"
                        on:change="{invalidate}"
                        maxlength="{maxInputLength.firstname}"
                    />
                </div>
                <div class="col-md-6">
                    <FormInputText
                        name="billing_lastname"
                        placeholder="{$_('checkout.lastname')} / {$_('checkout.company')}"
                        bind:value="{$checkoutData.billingAddress.lastname}"
                        required
                        on:change="{invalidate}"
                        maxlength="{maxInputLength.lastname}"
                    />
                </div>
            </div>
            <div class="row">
                <!-- <div class="col-md-12">
                    <FormInputText
                        name="billing_company"
                        placeholder="Unternehmen (optional)"
                        bind:value="{$checkoutData.billingAddress.company}"
                        on:change="{invalidate}"
                        maxlength="{maxInputLength.company}"
                    />
                </div> -->
                <div class="col-md-10">
                    <FormInputText
                        name="billing_street"
                        placeholder="{$_('checkout.street')}"
                        bind:value="{$checkoutData.billingAddress.street}"
                        required
                        on:change="{invalidate}"
                        maxlength="{maxInputLength.street}"
                    />
                </div>
                <div class="col-md-2">
                    <FormInputText
                        name="billing_houseNumber"
                        placeholder="{$_('checkout.house-number')}"
                        bind:value="{$checkoutData.billingAddress.houseNumber}"
                        required
                        on:change="{invalidate}"
                        maxlength="{maxInputLength.houseNumber}"
                    />
                </div>
                <div class="col-md-6">
                    <FormInputText
                        name="billing_postcode"
                        placeholder="{$_('checkout.postcode')}"
                        bind:value="{$checkoutData.billingAddress.postcode}"
                        required
                        on:change="{invalidate}"
                        pattern="^\d{'{5}'}$"
                        maxlength="{maxInputLength.postcode}"
                    />
                </div>
                <div class="col-md-6">
                    <FormInputText
                        name="billing_city"
                        placeholder="{$_('checkout.city')}"
                        bind:value="{$checkoutData.billingAddress.city}"
                        required
                        on:change="{invalidate}"
                        maxlength="{maxInputLength.city}"
                    />
                </div>
            </div>
            <div class="row">
                <div class="col-md-12">
                    <div class="select_holder">
                        <select
                            name="billing_country"
                            bind:value="{$checkoutData.billingAddress.countryCode}"
                            required
                            on:change="{invalidate}"
                        >
                            {#await getCountries({ valid_for_order: true }, "name." + initialLocale)}
                                <option value="{$checkoutData.billingAddress.countryCode}">
                                    <MiniLoader />
                                </option>
                            {:then countries}
                                {#each countries.data as country}
                                    <option
                                        value="{country.code}"
                                        selected="{country.code == $checkoutData.billingAddress.countryCode}"
                                    >
                                        {langField(country.name, "")}
                                    </option>
                                {/each}
                            {/await}
                        </select>
                        <div class="placeholder_overlay">Land</div>
                        <div class="right_overlay">
                            <ChevronDownIcon size="24px" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    {/if}

    {#if cartSum?.totalGross > 500 && $checkoutData?.payment?.type == "invoice"}
        <section>
            <div>
                {@html $_("checkout.invoice-possible-notice-html")}
            </div>
        </section>
    {/if}
    <CheckoutFooter
        backlink="/checkout"
        backtitle="{$_('checkout.back-to-shipping')}"
        nexttitle="weiter zur Zusammenfassung"
        submitDisabled="{!$checkoutData?.payment?.type ||
            ($checkoutData?.payment?.type == 'invoice' && cartSum?.totalGross > 500)}"
    />
</form>

<style lang="less"></style>
